import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ViewChild, inject } from '@angular/core';
import { LoaderService } from '@evc/platform';
import { ButtonComponent, IconComponent, ModalComponent } from '@evc/web-components';
import { finalize } from 'rxjs/operators';
import { GradeLibraryService } from '../services/grade-library.service';
import { GradeLibraryVersion } from '../interfaces/grade-library-version';

@Component({
  selector: 'app-inspection-setup-modal',
  templateUrl: './inspection-setup-modal.component.html',
  styleUrls: ['./inspection-setup-modal.component.scss'],
  imports: [CommonModule, ModalComponent, ButtonComponent, IconComponent],
})
export class InspectionSetupModalComponent extends ModalComponent implements AfterViewInit {
  @ViewChild('modal', { static: true }) public modal: ModalComponent;

  public readonly modalTitle = 'Inspection Setup';

  private readonly baseModalStyles: Record<string, string> = {
    'box-sizing': 'border-box',
    'border-radius': 'var(--radius-xl, 16px)',
    background: 'var(--fill-base, #FFF)',
    'box-shadow': '0 0 0 1px var(--effect-shadow-outline, rgba(0, 0, 0, 0.08)), 0 10px 16px -8px var(--effect-shadow-strong, rgba(0, 0, 0, 0.05)), 0 12px 32px 0 var(--effect-shadow-diffuse, rgba(0, 0, 0, 0.05))',
    'backdrop-filter': 'blur(25px)',
  };

  public get modalStyles(): Record<string, string> {
    return {
      ...this.baseModalStyles,
      width: '600px',
      height: 'auto',
      'min-height': this.hasVersion ? '481px' : '412px',
    };
  }

  public fileDisplayNameWip = '';
  public fileDisplayNameGSN = '';
  public saving = false;
  public isDragOverWip = false;
  public isDragOverGSN = false;

  private fileToUploadWip: File;
  private fileToUploadGSN: File;
  private settled = false;

  readonly loader = inject(LoaderService);
  readonly #gradeLibraryService = inject(GradeLibraryService);

  public version: GradeLibraryVersion;

  public get hasVersion(): boolean {
    return !!this.version && Number(this.version.version) > 0;
  }

  public get hasFileWip(): boolean {
    return !!this.fileDisplayNameWip;
  }

  public get hasFileGSN(): boolean {
    return !!this.fileDisplayNameGSN;
  }

  public override ngAfterViewInit(): void {
    this.modal.closeModal.subscribe(() => this.settle());
  }

  public get canSave(): boolean {
    return !!(this.fileToUploadWip || this.fileToUploadGSN) && !this.saving;
  }

  public handleFileUploadWip(files: FileList): void {
    this.fileToUploadWip = files.item(0);
    this.fileDisplayNameWip = this.fileToUploadWip ? this.fileToUploadWip.name : '';
  }

  public handleFileUploadGSN(files: FileList): void {
    this.fileToUploadGSN = files.item(0);
    this.fileDisplayNameGSN = this.fileToUploadGSN ? this.fileToUploadGSN.name : '';
  }

  public cancel(): void {
    this.modal.close();
  }

  public confirm(): void {
    if (!this.canSave) {
      return;
    }

    this.saving = true;
    this.loader.play();

    this.#gradeLibraryService
      .uploadFiles(this.fileToUploadWip, this.fileToUploadGSN)
      .pipe(finalize(() => {
        this.saving = false;
        this.loader.stop();
      }))
      .subscribe({
        next: (response: GradeLibraryVersion) => {
          if (!this.settled) {
            this.subject.next(response ? this.#gradeLibraryService.parseVersion(response) : null);
          }
          this.modal.close(true);
        },
        error: (error: unknown) => {
          if (!this.settled) {
            this.settled = true;
            this.subject.error(error);
          }
        },
      });
  }

  private settle(): void {
    if (this.settled) {
      return;
    }
    this.settled = true;
    this.subject.complete();
  }

  public onDragOver(event: DragEvent, target: 'wip' | 'gsn'): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragOver(target, true);
  }

  public onDragLeave(event: DragEvent, target: 'wip' | 'gsn'): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragOver(target, false);
  }

  public onDrop(event: DragEvent, target: 'wip' | 'gsn'): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragOver(target, false);

    const dropped = event.dataTransfer?.files;
    if (!dropped || dropped.length !== 1) {
      return;
    }

    if (target === 'wip') {
      this.fileToUploadWip = dropped[0];
      this.fileDisplayNameWip = dropped[0].name;
    } else {
      this.fileToUploadGSN = dropped[0];
      this.fileDisplayNameGSN = dropped[0].name;
    }
  }

  private setDragOver(target: 'wip' | 'gsn', value: boolean): void {
    if (target === 'wip') {
      this.isDragOverWip = value;
    } else {
      this.isDragOverGSN = value;
    }
  }
}